<template>
  <div class="fall-risk-wrapper">
    <!-- Badge clicável -->
    <div
      v-if="fallRisk && !loading"
      class="fall-risk-badge mr-2"
      :class="badgeClass"
      @click="openModal"
      title="Clique para ver o detalhamento"
    >
      <i :class="badgeIcon" class="mr-1"></i>
      <span>Queda: {{ badgeLabel }}</span>
      <span class="fall-risk-score">{{ fallRisk.score }}pts</span>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="fall-risk-badge fall-risk-loading">
      <b-spinner small class="mr-1" />
      <span>Calculando risco...</span>
    </div>

    <!-- Modal de detalhamento -->
    <div v-if="showModal" class="fall-risk-modal" @click.self="closeModal">
      <div class="fall-risk-modal-content">
        <!-- Header -->
        <div class="fall-risk-modal-header" :class="headerClass">
          <div class="fall-risk-modal-header-info">
            <i class="fas fa-walking mr-2" style="font-size: 24px"></i>
            <div>
              <h3 class="m-0">Score de Risco de Queda</h3>
              <p class="m-0" style="opacity: 0.9; font-size: 13px">
                Última atualização: {{ updatedAt }}
              </p>
            </div>
          </div>
          <button @click="closeModal" class="fall-risk-close-btn">
            &times;
          </button>
        </div>

        <!-- Classificação -->
        <div class="fall-risk-classification" :class="classificationClass">
          <div class="fall-risk-classification-icon">
            <i :class="badgeIcon"></i>
          </div>
          <div>
            <h2 class="m-0">{{ badgeLabel }}</h2>
            <p class="m-0" style="opacity: 0.9">
              Score total: {{ fallRisk.score }} pontos
            </p>
          </div>
        </div>

        <!-- Detalhamento dos pontos -->
        <div class="fall-risk-detail-section">
          <h4 class="fall-risk-section-title">
            <i class="fas fa-list-ul mr-2"></i>Detalhamento da Pontuação
          </h4>

          <div class="fall-risk-detail-grid">
            <!-- Idade -->
            <div
              class="fall-risk-detail-item"
              :class="detail.age_points > 0 ? 'item-alert' : 'item-ok'"
            >
              <div class="detail-label">
                <i class="fas fa-birthday-cake mr-1"></i>
                Idade ({{ detail.age }} anos)
              </div>
              <div
                class="detail-points"
                :class="detail.age_points > 0 ? 'points-alert' : 'points-ok'"
              >
                +{{ detail.age_points }}
              </div>
            </div>

            <!-- Histórico de quedas -->
            <div
              class="fall-risk-detail-item"
              :class="detail.fall_history_points > 0 ? 'item-alert' : 'item-ok'"
            >
              <div class="detail-label">
                <i class="fas fa-exclamation-triangle mr-1"></i>
                Quedas nos últimos 6 meses ({{ detail.falls_last_6_months }})
              </div>
              <div
                class="detail-points"
                :class="
                  detail.fall_history_points > 0 ? 'points-alert' : 'points-ok'
                "
              >
                +{{ detail.fall_history_points }}
              </div>
            </div>

            <!-- Mobilidade -->
            <div
              class="fall-risk-detail-item"
              :class="detail.mobility_points > 0 ? 'item-alert' : 'item-ok'"
            >
              <div class="detail-label">
                <i class="fas fa-wheelchair mr-1"></i>
                Mobilidade ({{ detail.mobility || "Nenhum" }})
              </div>
              <div
                class="detail-points"
                :class="
                  detail.mobility_points > 0 ? 'points-alert' : 'points-ok'
                "
              >
                +{{ detail.mobility_points }}
              </div>
            </div>

            <!-- Auxílio -->
            <div
              class="fall-risk-detail-item"
              :class="detail.assistance_points > 0 ? 'item-alert' : 'item-ok'"
            >
              <div class="detail-label">
                <i class="fas fa-hands-helping mr-1"></i>
                Auxílio para deambulação ({{
                  detail.assistance || "Independente"
                }})
              </div>
              <div
                class="detail-points"
                :class="
                  detail.assistance_points > 0 ? 'points-alert' : 'points-ok'
                "
              >
                +{{ detail.assistance_points }}
              </div>
            </div>

            <!-- Medicamentos -->
            <div
              class="fall-risk-detail-item"
              :class="detail.medication_points > 0 ? 'item-alert' : 'item-ok'"
            >
              <div class="detail-label">
                <i class="fas fa-pills mr-1"></i>
                Medicamentos de risco
                <span
                  v-if="
                    detail.medications_at_risk &&
                    detail.medications_at_risk.length > 0
                  "
                  class="med-list"
                >
                  ({{ detail.medications_at_risk.join(", ") }})
                </span>
              </div>
              <div
                class="detail-points"
                :class="
                  detail.medication_points > 0 ? 'points-alert' : 'points-ok'
                "
              >
                +{{ detail.medication_points }}
              </div>
            </div>
          </div>

          <!-- Total -->
          <div class="fall-risk-total" :class="totalClass">
            <span>Score Total</span>
            <span class="fall-risk-total-value"
              >{{ fallRisk.score }} pontos</span
            >
          </div>

          <!-- Legenda -->
          <div class="fall-risk-legend">
            <span class="legend-item legend-verde">0–3 Baixo</span>
            <span class="legend-item legend-amarelo">4–7 Moderado</span>
            <span class="legend-item legend-vermelho">8+ Alto</span>
          </div>
        </div>

        <!-- Footer com botão recalcular -->
        <div class="fall-risk-modal-footer">
          <small class="text-muted">
            <i class="fas fa-info-circle mr-1"></i>
            Score calculado com base nos dados cadastrados
          </small>
          <v-btn
            class="fall-risk-recalculate-btn"
            small
            :loading="recalculating"
            :disabled="recalculating"
            @click="recalculate"
          >
            <i class="fas fa-sync-alt mr-1"></i>
            Recalcular
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Overlay -->
    <div v-if="showModal" class="fall-risk-overlay"></div>
  </div>
</template>

<script>
import api from "../../services/api";
import moment from "moment";

export default {
  name: "FallRiskBadge",

  props: {
    resident_id: [String, Number],
  },

  data() {
    return {
      loading: false,
      recalculating: false,
      showModal: false,
      fallRisk: null,
    };
  },

  computed: {
    detail() {
      if (!this.fallRisk || !this.fallRisk.detail) return {};
      return this.fallRisk.detail;
    },

    badgeClass() {
      const level = this.fallRisk && this.fallRisk.level;
      if (level === "vermelho") return "badge-vermelho";
      if (level === "amarelo") return "badge-amarelo";
      return "badge-verde";
    },

    headerClass() {
      const level = this.fallRisk && this.fallRisk.level;
      if (level === "vermelho") return "header-vermelho";
      if (level === "amarelo") return "header-amarelo";
      return "header-verde";
    },

    classificationClass() {
      const level = this.fallRisk && this.fallRisk.level;
      if (level === "vermelho") return "classification-vermelho";
      if (level === "amarelo") return "classification-amarelo";
      return "classification-verde";
    },

    totalClass() {
      const level = this.fallRisk && this.fallRisk.level;
      if (level === "vermelho") return "total-vermelho";
      if (level === "amarelo") return "total-amarelo";
      return "total-verde";
    },

    badgeLabel() {
      const level = this.fallRisk && this.fallRisk.level;
      if (level === "vermelho") return "Alto";
      if (level === "amarelo") return "Moderado";
      return "Baixo";
    },

    badgeIcon() {
      const level = this.fallRisk && this.fallRisk.level;
      if (level === "vermelho") return "fas fa-exclamation-triangle";
      if (level === "amarelo") return "fas fa-exclamation-circle";
      return "fas fa-check-circle";
    },

    updatedAt() {
      if (!this.fallRisk || !this.fallRisk.updated_at) return "—";
      return moment(this.fallRisk.updated_at).format("DD/MM/YYYY HH:mm");
    },
  },

  created() {
    this.loadScore();
  },

  methods: {
    async loadScore() {
      try {
        this.loading = true;
        const { resident_id } = this;
        const response = await api.get(`/ai/smart/summary/${resident_id}`);
        if (response.data.success) {
          this.fallRisk = response.data.fall_risk;
        }
      } catch (error) {
        console.error("[FallRiskBadge] erro ao carregar score:", error);
      } finally {
        this.loading = false;
      }
    },

    async recalculate() {
      try {
        this.recalculating = true;
        const { resident_id } = this;
        const response = await api.post(
          `/ai/smart/summary/${resident_id}/recalculate-fall-risk`
        );
        if (response.data.success) {
          this.fallRisk = response.data.fall_risk;
          this.$toast.success("Score recalculado com sucesso!");
        } else {
          this.$toast.error("Erro ao recalcular score.");
        }
      } catch (error) {
        console.error("[FallRiskBadge] erro ao recalcular:", error);
        this.$toast.error("Erro ao recalcular. Tente novamente.");
      } finally {
        this.recalculating = false;
      }
    },

    openModal() {
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
    },
  },
};
</script>

<style scoped>
/* Badge */
.fall-risk-wrapper {
  display: inline-block;
  margin-top: 4px;
}

.fall-risk-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 2px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  user-select: none;
}

.fall-risk-badge:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.fall-risk-score {
  background: rgba(255, 255, 255, 0.9);
  padding: 1px 6px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 700;
  margin-left: 2px;
}

.badge-verde {
  background: linear-gradient(135deg, #d1fae5 0%, #a7f3d0 100%);
  color: #065f46;
  border: 1px solid #6ee7b7;
}

.badge-amarelo {
  background: linear-gradient(135deg, #fef3c7 0%, #fed7aa 100%);
  color: #92400e;
  border: 1px solid #f59e0b;
}

.badge-vermelho {
  background: linear-gradient(135deg, #fee2e2 0%, #fecaca 100%);
  color: #991b1b;
  border: 1px solid #f87171;
}

.fall-risk-loading {
  background: #f1f5f9;
  color: #64748b;
  border: 1px solid #cbd5e1;
  cursor: default;
}

.fall-risk-loading:hover {
  transform: none;
  box-shadow: none;
}

/* Overlay */
.fall-risk-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
}

/* Modal */
.fall-risk-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.fall-risk-modal-content {
  background: white;
  border-radius: 16px;
  max-width: 560px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-30px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Header do modal */
.fall-risk-modal-header {
  padding: 20px 24px;
  border-radius: 16px 16px 0 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: white;
}

.fall-risk-modal-header-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.fall-risk-modal-header h3 {
  font-size: 1.2rem;
  font-weight: 600;
}

.header-verde {
  background: linear-gradient(135deg, #059669, #047857);
}
.header-amarelo {
  background: linear-gradient(135deg, #d97706, #b45309);
}
.header-vermelho {
  background: linear-gradient(135deg, #dc2626, #b91c1c);
}

.fall-risk-close-btn {
  background: none;
  border: none;
  color: white;
  font-size: 2rem;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background 0.2s;
  line-height: 1;
}

.fall-risk-close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

/* Classificação */
.fall-risk-classification {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 20px 24px;
  color: white;
}

.fall-risk-classification-icon {
  font-size: 2rem;
}

.fall-risk-classification h2 {
  font-size: 1.8rem;
  font-weight: 700;
}

.classification-verde {
  background: linear-gradient(135deg, #ecfdf5, #d1fae5);
  color: #065f46;
}
.classification-amarelo {
  background: linear-gradient(135deg, #fffbeb, #fef3c7);
  color: #92400e;
}
.classification-vermelho {
  background: linear-gradient(135deg, #fff1f2, #fee2e2);
  color: #991b1b;
}

/* Detalhamento */
.fall-risk-detail-section {
  padding: 20px 24px;
}

.fall-risk-section-title {
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 12px;
}

.fall-risk-detail-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
}

.fall-risk-detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 14px;
  border-radius: 8px;
  font-size: 13px;
}

.item-ok {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
}

.item-alert {
  background: #fff7ed;
  border: 1px solid #fed7aa;
}

.detail-label {
  color: #374151;
  flex: 1;
}

.med-list {
  color: #6b7280;
  font-size: 12px;
}

.detail-points {
  font-weight: 700;
  font-size: 14px;
  min-width: 32px;
  text-align: right;
}

.points-ok {
  color: #059669;
}
.points-alert {
  color: #d97706;
}

/* Total */
.fall-risk-total {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 15px;
  margin-bottom: 12px;
}

.total-verde {
  background: #d1fae5;
  color: #065f46;
}
.total-amarelo {
  background: #fef3c7;
  color: #92400e;
}
.total-vermelho {
  background: #fee2e2;
  color: #991b1b;
}

.fall-risk-total-value {
  font-size: 18px;
  font-weight: 700;
}

/* Legenda */
.fall-risk-legend {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.legend-item {
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
}

.legend-verde {
  background: #d1fae5;
  color: #065f46;
}
.legend-amarelo {
  background: #fef3c7;
  color: #92400e;
}
.legend-vermelho {
  background: #fee2e2;
  color: #991b1b;
}

/* Footer */
.fall-risk-modal-footer {
  padding: 16px 24px;
  background: #f9fafb;
  border-top: 1px solid #e5e7eb;
  border-radius: 0 0 16px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.fall-risk-recalculate-btn {
  background: #0f65b0 !important;
  color: white !important;
  border: none !important;
  border-radius: 8px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  letter-spacing: 0 !important;
}

/* Responsivo */
@media (max-width: 576px) {
  .fall-risk-modal {
    padding: 10px;
  }

  .fall-risk-modal-content {
    max-height: 95vh;
  }

  .fall-risk-modal-footer {
    flex-direction: column;
    text-align: center;
  }
}
</style>